// tslint:disable: no-inferrable-types
import { ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { Transition } from '../lib/transition.model';
import { TransitionGuard } from '../lib/transition-guard.model';
import { TransitionAction } from '../lib/transition-action.model';

import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import {
  WorkflowTransitionGuardFormComponent,
  WorkflowTransitionGuardFormOptions,
  WorkflowTransitionGuardFormResult
} from './transition-guard/guard-form.component';
import {
  WorkflowTransitionActionFormComponent,
  WorkflowTransitionActionFormOptions,
  WorkflowTransitionActionFormResult
} from './transition-action/action-form.component';
@Component({
    selector: 'eas-workflow-transition-item',
    templateUrl: './transition-item.component.html',
    styleUrls: ['./transition-item.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WorkflowTransitionItemComponent {

  @Input() availableGuards: TransitionGuard[] = [];
  @Input() availableActions: TransitionAction[] = [];
  @Input() item!: Transition;
  @Input() isFirst: boolean = false;
  @Input() isLast: boolean = false;
  @Output() deleteTransition = new EventEmitter<string>();
  @Output() editTransition = new EventEmitter<string>();
  @Output() patchTransition = new EventEmitter<Partial<Transition>>();
  @Output() focusTransition = new EventEmitter<string|undefined>();

  private formDialogRef?:
    MatDialogRef<WorkflowTransitionGuardFormComponent, WorkflowTransitionGuardFormResult> |
    MatDialogRef<WorkflowTransitionActionFormComponent, WorkflowTransitionActionFormResult>;

  constructor(private dialog: MatDialog, private injector: Injector) { }

  //
  // Actions
  //

  public onMouseEnter(): void {
    this.focusTransition.emit(this.item.id);
  }

  public onMouseLeave(): void {
    this.focusTransition.emit(undefined);
  }

  public onEdit(): void {
    if (!this.canEdit) { return; }
    this.editTransition.emit(this.item.id);
  }

  public onDelete(): void {
    if (!this.canDelete) { return; }
    this.deleteTransition.emit(this.item.id);
  }

  public onAddGuard(): void {
    this.presentGuardFormDialog(undefined, (result) => {
      if (!result) { return; }
      if (!(result instanceof TransitionGuard)) { return; }
      this.onDidEditGuards([...this.item.guards, result]);
    });
  }

  public onEditGuard(guardID: string): void {
    const guard = this.item.guards.find(g => g.id === guardID);
    if (!guard) { return; }

    this.presentGuardFormDialog(guard, (result) => {
      if (!result) { return; }

      // Editing of the guard, result is the new guard
      if (result instanceof TransitionGuard) {
        const guards = this.item.guards.filter(g => g.id !== result.id);
        this.onDidEditGuards([...guards, result]);
        return;
      }

      // Delete of the guard, result is the ID to delete
      if (typeof result === 'string') {
        const guards = this.item.guards.filter(g => g.id !== result);
        this.onDidEditGuards(guards);
        return;
      }
    });
  }

  public onDidEditGuards(guards: TransitionGuard[]): void {
    const update: Partial<Transition> = { id: this.item.id, 'guards': guards };
    this.patchTransition.emit(update);
  }

  private presentGuardFormDialog(guard: TransitionGuard | undefined,
                                 afterClosed?: (result: WorkflowTransitionGuardFormResult) => void): void {
    if (this.formDialogRef) { return; }

    const options: MatDialogConfig<WorkflowTransitionGuardFormOptions> = {
      minWidth: '50vw',
      // minHeight: '30vh',
      id: 'workflow-editor-form-dialog',
      data: {
        item: guard,
        templates: this.availableGuards,
        callerInjector: this.injector
      }
    };

    this.formDialogRef =
      this.dialog.open<WorkflowTransitionGuardFormComponent,
                       WorkflowTransitionGuardFormOptions,
                       WorkflowTransitionGuardFormResult>(WorkflowTransitionGuardFormComponent, options);
    this.formDialogRef.afterClosed().subscribe(result => {
      this.formDialogRef = undefined;
      if (afterClosed && result) { afterClosed(result); }
    });
  }

  public onAddAction(): void {
    this.presentActionFormDialog(undefined, (result) => {
      if (!result) { return; }
      if (!(result instanceof TransitionAction)) { return; }
      this.onDidEditActions([...this.item.actions, result]);
    });
  }

  public onEditAction(actionID: string): void {
    const action = this.item.actions.find(a => a.id === actionID);
    if (!action) { return; }

    this.presentActionFormDialog(action, (result) => {
      if (!result) { return; }

      if (result instanceof TransitionAction) {
        const actions = this.item.actions.filter(a => a.id !== result.id);
        this.onDidEditActions([...actions, result]);
        return;
      }

      if (typeof result === 'string') {
        const actions = this.item.actions.filter(a => a.id !== actionID);
        this.onDidEditActions(actions);
        return;
      }
    });
  }

  public onDidEditActions(actions: TransitionAction[]): void {
    const update: Partial<Transition> = { id: this.item.id, 'actions': actions };
    this.patchTransition.emit(update);
  }

  private presentActionFormDialog(action: TransitionAction | undefined,
                                  afterClosed?: (result: WorkflowTransitionActionFormResult) => void): void {
    if (this.formDialogRef) { return; }
    const usedActions = this.item.actions.map(itm => itm.type);
    const availableActions = this.availableActions.filter(itm => {
      // Always preserve the current one
      if (action !== undefined && itm.type === action.type) {
        return true;
      }
      return !usedActions.includes(itm.type);
    });

    const options: MatDialogConfig<WorkflowTransitionActionFormOptions> = {
      minWidth: '50vw',
      maxWidth: '60vw',
      id: 'workflow-editor-form-dialog',
      data: {
        item: action,
        templates: availableActions,
        callerInjector: this.injector
      }
    };

    this.formDialogRef =
      this.dialog.open<WorkflowTransitionActionFormComponent,
                       WorkflowTransitionActionFormOptions,
                       WorkflowTransitionActionFormResult>(WorkflowTransitionActionFormComponent, options);
    this.formDialogRef.afterClosed().subscribe(result => {
      this.formDialogRef = undefined;
      if (afterClosed && result) { afterClosed(result); }
    });
  }

  //
  // Template helpers
  //
  public roles(): string {
    return this.item.roles.map(r => r.name).join(', ');
  }

  public get shouldShowSuccessMessage(): boolean {
    return !this.item.isInitialTransition;
  }

  public get shouldShowAutoTransition(): boolean {
    return !this.item.isInitialTransition;
  }

  public get shouldShowValidForm(): boolean {
    return !this.item.isInitialTransition;
  }

  public get shouldShowConfirmation(): boolean {
    return !this.item.isInitialTransition;
  }

  public get shouldShowActions(): boolean {
    return this.item.actions.length > 0;
  }

  public get shouldShowGuards(): boolean {
    return !this.item.isInitialTransition && this.item.guards.length > 0;
  }

  public get canAddGuards(): boolean {
    return !this.item.isInitialTransition;
  }

  public get canEdit(): boolean {
    return true;
  }

  public get canDelete(): boolean {
    return !this.item.isInitialTransition;
  }
}
